import BackendAuthHandler from 'BackendAuthHandler';
import TokenData from 'TokenData';
import { NOT_FOUND } from './constants';
import {
  getAssetStorageAccessToken,
  getInFlightAssetStorageSetup,
  normalizeSetupAssetStoragePayload,
  optionalReactAppEnv,
  setInFlightAssetStorageSetup,
} from './tokenUtils';
import { isDocumentPageRasterPath } from '../documentPageRasterPath';
import type { StorageItem, StorageListResponse } from './types';
import { BaseBackendServicePairing } from './BaseBackendServicePairing';

/**
 * Object URLs for downloaded images, shared by every BackendService instance and kept for the
 * page's lifetime: the authenticated GCS fetch bypasses the browser's image cache, so without this
 * each card that renders the same image downloads it again. Only images/ paths are cached —
 * callers never revoke those URLs (document downloads do, so they stay uncached).
 */
const imageObjectUrlCache = new Map<string, Promise<string>>();

export class BaseBackendServiceAssets extends BaseBackendServicePairing {
  getApplicationURL(modelType: string, modelId: string): Promise<string> {
    return this.setupAssetStorageWithRetry(false)
      .then((tokenData) =>
        this.getAsset(`application/${modelType}/${modelId}.pdf`, tokenData.token.token, {
          url: true,
        }),
      )
      .then((url) => url as string);
  }

  waitForAssetStorage(maxRetries: number = 3): Promise<boolean> {
    return this.waitForAssetStorageAsync(maxRetries);
  }

  private async waitForAssetStorageAsync(maxRetries: number = 3): Promise<boolean> {
    console.log('Waiting for asset storage');
    const localToken = localStorage.getItem('assetStorageToken');

    if (localToken) {
      try {
        const tokenData = normalizeSetupAssetStoragePayload(JSON.parse(localToken));
        const expireTime = tokenData.token?.res?.data?.expireTime;

        if (expireTime) {
          const expireTimeDate = new Date(expireTime);
          if (expireTimeDate.getTime() - Date.now() > 5000) {
            console.log('Asset storage token is valid');
            return true;
          }
          console.log('Asset storage token has expired');
        } else if (getAssetStorageAccessToken(tokenData)) {
          console.log('Asset storage token is valid (no expiry metadata)');
          return true;
        } else {
          console.log('Asset storage token has no expire time');
        }
      } catch (error) {
        console.error('Error parsing asset storage token:', error);
      }
    } else {
      console.log('No asset storage token found in localStorage');
    }

    console.log('Asset storage token not available or expired, setting up new token');
    try {
      await this.setupAssetStorageWithRetry(true, maxRetries);
      console.log('Asset storage setup successful');
      return true;
    } catch (error) {
      console.error('Failed to setup asset storage after retries:', error);
      return false;
    }
  }

  setupAssetStorageWithRetry(forceRetrieve: boolean, maxRetries: number = 3): Promise<TokenData> {
    const inFlight = getInFlightAssetStorageSetup(forceRetrieve);
    if (inFlight) {
      return inFlight;
    }

    const promise = this.runSetupAssetStorageWithRetry(forceRetrieve, maxRetries).finally(() => {
      setInFlightAssetStorageSetup(forceRetrieve, null);
    });

    setInFlightAssetStorageSetup(forceRetrieve, promise);
    return promise;
  }

  private async runSetupAssetStorageWithRetry(
    forceRetrieve: boolean,
    maxRetries: number,
  ): Promise<TokenData> {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
      try {
        return await this.setupAssetStorage(forceRetrieve);
      } catch (error) {
        console.error(`Asset storage setup attempt ${attempt} failed:`, error);
        lastError = error;
        if (attempt < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
    }
    throw lastError;
  }

  private async setupAssetStorage(forceRetrieve?: boolean): Promise<TokenData> {
    if (!forceRetrieve) {
      const localToken = localStorage.getItem('assetStorageToken');

      if (localToken) {
        const tokenData = normalizeSetupAssetStoragePayload(JSON.parse(localToken));
        const expireTime = tokenData.token?.res?.data?.expireTime;

        if (expireTime) {
          const expireTimeDate = new Date(expireTime);
          if (expireTimeDate.getTime() - Date.now() > 5000) {
            return tokenData;
          }
        } else if (getAssetStorageAccessToken(tokenData)) {
          return tokenData;
        }
      }
    }

    const token = await new BackendAuthHandler().withAuthToken();
    const response = await this.authenticatedFetch(this.apiURL('setupAssetStorage'), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await response.json();
    const normalized = normalizeSetupAssetStoragePayload(data);
    localStorage.setItem('assetStorageToken', JSON.stringify(normalized));
    return normalized;
  }

  getAssetByURL(
    path: string,
    options: { url?: boolean; listItems?: boolean; base64?: false },
  ): Promise<string | StorageItem[]> {
    if (isDocumentPageRasterPath(path)) {
      return this.getDocumentPageRaster(path, options);
    }
    return this.setupAssetStorageWithRetry(false).then((token) =>
      this.getAsset(path, token.token.token, options),
    );
  }

  /**
   * Page rasters are an authenticated API (lazy render + stream PNG), not a GCS
   * object. Same pattern as other /api images: Bearer fetch → object URL.
   */
  private getDocumentPageRaster(
    path: string,
    options: { url?: boolean; listItems?: boolean; base64?: false },
  ): Promise<string | StorageItem[]> {
    return this.authFetchPromise(async (token) => {
      const response = await this.authenticatedFetch(this.apiURL(path.replace(/^\/api\//, '')), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 404) {
          throw NOT_FOUND;
        }
        const text = await response.text().catch(() => '');
        throw text
          ? `${response.status}: Network response was not ok: ${text}`
          : 'Network response was not ok';
      }
      const blob = await response.blob();
      if (options.base64) {
        return this.readBlobAsDataURL(blob);
      }
      return URL.createObjectURL(blob);
    });
  }

  getAssetPresignedURL(path: string): Promise<string> {
    return this.getAssetPresignedURLAsync(path);
  }

  private async getAssetPresignedURLAsync(path: string): Promise<string> {
    const token = await new BackendAuthHandler().withAuthToken();
    console.log('BaseBackendService: Getting presigned URL for path:', path);

    if (path.startsWith('images/FundOpportunity/')) {
      console.log('BaseBackendService: Using /api/images/ endpoint for FundOpportunity image');
      const response = await this.authenticatedFetch(
        `/api/presignedImage/${path.replace(/^images\//, '')}?returnUrl=true&expiryInMinutes=10`,
        {
          method: 'GET',
          redirect: 'manual',
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log('BaseBackendService: /api/presignedImage/ response status:', response.status);
      if (response.ok) {
        console.log('BaseBackendService: No redirect, using direct URL');
        const data = await response.json();
        return data.url;
      }
      throw new Error(`Failed to get presigned URL: ${response.status}`);
    }

    console.log('BaseBackendService: Using direct GCS approach for non-FundOpportunity image');
    const tokenObj = await this.setupAssetStorageWithRetry(false);
    const baseUrl = `https://storage.googleapis.com/storage/v1/b/${process.env.REACT_APP_STORAGE_BUCKET}/o/${encodeURIComponent(path)}`;
    const presignedUrl = `${baseUrl}?alt=media&access_token=${tokenObj.token.token}`;
    console.log('BaseBackendService: Generated storage URL:', presignedUrl);
    return presignedUrl;
  }

  protected storageAssetURL(path: string): string {
    const base = optionalReactAppEnv('REACT_APP_STORAGE_BASE_URL');
    if (base) {
      // A query-string-style base (e.g. designer mode's `/api/designer-asset?path=`)
      // must have the encoded path appended directly — joining it with `/` first
      // puts a stray leading slash inside the `path` query value itself.
      if (base.includes('?')) {
        return `${base}${encodeURIComponent(path)}`;
      }
      return `${base.replace(/\/$/, '')}/${encodeURIComponent(path)}`;
    }
    const bucket = process.env.REACT_APP_STORAGE_BUCKET;
    return `https://storage.googleapis.com/storage/v1/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`;
  }

  protected storageListURL(prefix: string): string {
    const base = optionalReactAppEnv('REACT_APP_STORAGE_BASE_URL');
    if (base) {
      return `${base.replace(/\/$/, '')}/list?prefix=${encodeURIComponent(prefix)}`;
    }
    const bucket = process.env.REACT_APP_STORAGE_BUCKET;
    return `https://storage.googleapis.com/storage/v1/b/${bucket}/o?prefix=${encodeURIComponent(prefix)}&maxResults=1000`;
  }

  getAsset(
    path: string,
    token: string,
    options: { url?: boolean; listItems?: boolean; base64?: false },
  ): Promise<string | StorageItem[]> {
    if (options.url && !options.base64 && !options.listItems && path.startsWith('images/')) {
      let cached = imageObjectUrlCache.get(path);
      if (!cached) {
        cached = this.getAssetAsync(path, token, options) as Promise<string>;
        imageObjectUrlCache.set(path, cached);
        cached.catch(() => imageObjectUrlCache.delete(path));
      }
      return cached;
    }
    return this.getAssetAsync(path, token, options);
  }

  private readBlobAsDataURL(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }

  private async getAssetAsync(
    path: string,
    token: string,
    options: { url?: boolean; listItems?: boolean; base64?: false },
  ): Promise<string | StorageItem[]> {
    const url = options.listItems ? this.storageListURL(path) : this.storageAssetURL(path);
    if (options.listItems) {
      options.url = false;
    }

    const response = await this.authenticatedFetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      if (response.status == 404) {
        throw NOT_FOUND;
      }
      const text = await response.text().catch(() => '');
      throw text
        ? `${response.status}: Network response was not ok: ${text}`
        : 'Network response was not ok';
    }

    if (options.url) {
      const blob = await response.blob();
      if (options.base64) {
        return this.readBlobAsDataURL(blob);
      }
      return URL.createObjectURL(blob);
    }

    if (options.listItems) {
      return this.fetchAllStorageListItems(url, token, await response.json());
    }

    const data = await response.text();
    if (!data) {
      throw NOT_FOUND;
    }
    return data === '"null"' ? '' : data;
  }

  private async fetchAllStorageListItems(
    url: string,
    token: string,
    firstPage: StorageListResponse,
  ): Promise<StorageItem[]> {
    const items = [...(firstPage.items || [])];
    let nextToken = firstPage.nextPageToken;

    if (items.length === 0) {
      throw NOT_FOUND;
    }

    while (nextToken) {
      const response = await this.authenticatedFetch(`${url}&pageToken=${nextToken}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data: StorageListResponse = await response.json();
      items.push(...data.items);
      nextToken = data.nextPageToken;
    }

    return items;
  }

  getStaticPDF(modelType: string, modelId: string): Promise<string | null> {
    return this.setupAssetStorage()
      .then((tokenData) =>
        this.getAsset(`staticPDFs/${modelType}/${modelId}/static.pdf`, tokenData.token.token, {
          url: true,
        }),
      )
      .then((url) => url as string)
      .catch((error: any) => (error == NOT_FOUND ? null : Promise.reject(error)));
  }
}
